'use client';

import { Select } from '@ironhack/design-system2/components/select';
import { useRouter } from 'next/navigation';
import { type ReactElement, useCallback } from 'react';

import {
  type FullParams,
  type PageType,
  getPageUrl,
  isPageIndexable,
} from '@/app/urls/url-mapping';
import { type DatoRegion } from '@/lib/datocms';
import { type Language, getLanguageFromLocale } from '@/lib/utils/language';
import { sanitizeAlternateParams } from '@/lib/utils/meta-tags';
import { type Region, type RegionLanguageMap } from '@/types';

type RegionSelectProps = {
  page: PageType;
  params: FullParams;
  region: DatoRegion;
  regionOptions?: Partial<RegionLanguageMap>;
  regions: DatoRegion[];
};

export const RegionSelect = (props: RegionSelectProps): ReactElement => {
  const { page, params, region, regionOptions, regions } = props;
  const router = useRouter();

  const getUrl = useCallback(
    (code: Region, language: Language): string | undefined => {
      const url = regionOptions?.[code]?.[language];
      if (url) return url;

      const baseParams = { language, region: code };
      const pageParams = sanitizeAlternateParams(params);

      if (page.includes('blog')) return getPageUrl('blog', baseParams);
      return (
        getPageUrl(page, { ...pageParams, ...baseParams }) ??
        getPageUrl('home', baseParams)
      );
    },
    [page, params, regionOptions]
  );

  const handleRegionChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const regionCode = event.target.value;
      if (regionCode === region.code) return;

      const newRegion = regions.find((r) => r.code === regionCode);
      if (!newRegion) return;

      const language = getLanguageFromLocale(newRegion.mainLanguage.code);
      const newUrl = getUrl(newRegion.code, language);
      if (newUrl) router.push(newUrl);
    },
    [getUrl, region.code, regions, router]
  );

  const seoRegionLinks = regions.map(({ name, code, mainLanguage }) => {
    const language = getLanguageFromLocale(mainLanguage.code);
    return (
      isPageIndexable(page, { ...params, language, region: code }) && {
        code,
        href: getUrl(code, language),
        name,
      }
    );
  });

  return (
    <>
      <Select
        color="text.primary"
        mb={4}
        onChange={handleRegionChange}
        value={region.code}
        w="full"
      >
        {regions.map(({ name, code }) => (
          <option key={code} value={code}>
            {name}
          </option>
        ))}
      </Select>
      {isPageIndexable(page, params) &&
        // Render hidden links to the possible region pages for SEO crawlers
        seoRegionLinks.map(
          (link) =>
            link && (
              <a href={link.href} key={link.code} style={{ display: 'none' }}>
                {link.name}
              </a>
            )
        )}
    </>
  );
};
